import React, { useEffect } from 'react'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import LibraryBooksOutlinedIcon from 'components/material-symbol/icons/LibraryBooks'
import FileCopyOutlinedIcon from 'components/material-symbol/icons/FileCopy'
import BuildOutlinedIcon from 'components/material-symbol/icons/Build'
import Card from '@material-ui/core/Card'
import Button from '@material-ui/core/Button'
import { makeStyles } from '@material-ui/core/styles'
import { useFormContext } from 'react-hook-form'
import { useWizardFormContext } from 'components/wizard-form/context'
import { gtmEvent } from 'lib/gtm'
import CircularHighlightedIcon from 'components/circular-highlighted-icon'
import CreateFromTemplateDialog from 'components/group-events/create-group-event-form-wizard/dialogs/create-from-template-dialog'
import CopyExistingEventDialog from 'components/group-events/create-group-event-form-wizard/dialogs/copy-existing-event-dialog'
import { useHydrateEventFormValues } from 'components/group-events/create-group-event-form-wizard/hooks'

export const useStyles = makeStyles(theme => ({
  tilesRow: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
    gap: theme.spacing(2),
    width: '100%',
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  actionTileWrapper: {
    flex: '1 1 300px',
    maxWidth: '375px',
    width: '100%',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0, 35, 75, 0.04)',
    },
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 auto',
      maxWidth: '100%',
    },
  },
  headlineText: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '16px',
      margin: '12px',
    },
  },
  tonalButton: {
    color: '#172024',
    backgroundColor: '#EBF0F2',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#DDE4E7',
      boxShadow: 'none',
      color: '#172024',
    },
  },
}))

const TileButtons = ({
  icon,
  headlineText,
  captionText,
  buttonText,
  onClick,
}) => {
  const classes = useStyles()
  return (
    <Card
      variant="outlined"
      className={classes.actionTileWrapper}
      onClick={() => {
        gtmEvent({
          event: 'formSubmit',
          formCategory: 'group-event-creation',
          formAction: headlineText.toLowerCase().replace(/\s+/g, '-'),
        })
        onClick && onClick()
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="space-between"
        p={2}
      >
        <CircularHighlightedIcon icon={icon} />
        <Box my={0.5}>
          <Typography variant="h6">{headlineText}</Typography>
        </Box>
        <Box mb={3} textAlign="center">
          <Typography variant="body2" color="textSecondary">
            {captionText}
          </Typography>
        </Box>
        <Button
          variant="contained"
          className={classes.tonalButton}
          onClick={onClick}
        >
          {buttonText}
        </Button>
      </Box>
    </Card>
  )
}

const GetStarted = ({ canBuildOwnEvent }) => {
  const { register } = useFormContext()
  const { advance } = useWizardFormContext()
  const classes = useStyles()
  const hydrateEventFormValues = useHydrateEventFormValues()

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const timer = setTimeout(() => {
      window.location.search.includes('preload') && hydrateEventFormValues()
    }, 250)
    return () => clearTimeout(timer)
  }, [])
  /* eslint-enable react-hooks/exhaustive-deps */

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <input type="hidden" name="templateId" ref={register} />
      <Box mb={4}>
        <Typography variant="h4" align="center" data-cy="get-started-heading">
          How would you like to create your event?
        </Typography>
      </Box>
      <Box className={classes.tilesRow}>
        <CreateFromTemplateDialog
          trigger={
            <TileButtons
              icon={LibraryBooksOutlinedIcon}
              headlineText="Create from Template"
              captionText="Modify an existing group event template"
              buttonText="Create From Template"
            />
          }
        />
        <CopyExistingEventDialog
          trigger={
            <TileButtons
              icon={FileCopyOutlinedIcon}
              headlineText="Copy Existing Event"
              captionText="Copy and edit an existing event"
              buttonText="Copy Existing Event"
            />
          }
          isFreeTierReached={!canBuildOwnEvent}
        />
        {canBuildOwnEvent && (
          <TileButtons
            onClick={advance}
            icon={BuildOutlinedIcon}
            headlineText="Build Your Own"
            captionText="Customize your own group event"
            buttonText="Build Your Own"
          />
        )}
      </Box>
    </Box>
  )
}

export default GetStarted
